/**
 * Automation Requests State (Solicitudes tab — TV1-2377 slice 7)
 *
 * The seller's view of what happened to what she asked for. The grain is the
 * SUB-GOAL, exactly as the readmodel's is (P6): one request fans out into N
 * sub-goals, each with its own status, its own review outcome and its own rule —
 * or, quite legitimately, no rule at all.
 *
 * ⚠️ REQUESTS IN FLIGHT ARE NOT RULES. Nothing here belongs in the Reglas
 * catalog: under D1 no rule exists until a human approves, and an unsatisfiable
 * sub-goal never becomes one. This store therefore holds readmodel rows from
 * `/automation-requests`, never `RulesReadmodelType`.
 */

import { AutomationRequestsReadmodelType, RulesExecutionsReadmodelType } from '@bett3r-dev/teselly-domain';

/**
 * What we can honestly say about a Sub-Goal's SHADOW observations.
 *
 * Modelled as a discriminated union rather than `{ count: number }` because the
 * three states must not be collapsible by accident:
 *
 *  - `loading`  — we have not looked yet. Says nothing.
 *  - `no-rule`  — NO rule is watching. Either the approval-time derivation
 *    refused (it currently only LOGS — an approved Sub-Goal may have no rule at
 *    all, ever), or the rule id names a rule that was never minted. This is a
 *    real ABSENCE, not "we don't know where to look": the id is derived with the
 *    same `ruleIdForSubGoal` the server binds with, so it is looked up whether or
 *    not promotion has stamped `ruleId` onto the row. Rendering "0 firings" here
 *    would promise a watcher that does not exist.
 *  - `observed` — a rule exists; `firingCount` is a real measurement over the
 *    window that opened at `shadowSince` (the shadow window begins at APPROVAL,
 *    D1 — never at the request's submission).
 */
export type ShadowEvidence =
  | { status: 'loading' }
  | { status: 'no-rule' }
  | {
    status: 'observed';
    ruleId: string;
    /** When the shadow window opened. Stamped at BINDING time, i.e. at approval. */
    shadowSince?: string;
    /** Firings observed since `shadowSince`. ZERO IS A RESULT, not an absence of one. */
    firingCount: number;
    /** The most recent firings, newest first. Empty when `firingCount` is 0. */
    recent: RulesExecutionsReadmodelType[];
    /**
     * The rule's Declared Input values, keyed by input name, as read off the Rules
     * row. The promote gate checks the approved graph's declared inputs against
     * these (`unfilledInputsForPromotion`, TV1-2453).
     */
    declaredInputsValues?: Record<string, unknown>;
    /**
     * The rule's Declared Inputs with the options the Rules by-id read materializes
     * for them. Read only for an `approved` Sub-Goal with an unfilled input, and absent
     * when that read failed: the request page offers a select per unfilled input from
     * these (TV1-2453 slice 8).
     */
    declaredInputs?: DeclaredInputOptions[];
  };

/**
 * One option of a Declared Input, as the Rules by-id read materializes it. `value`
 * is what gets stored: a string, or an object such as
 * `{ source: 'warehouses', warehouseId }`.
 */
export type DeclaredInputOption = { value: unknown; label?: string };

/** A Declared Input and its materialized options, grouped by option kind. */
export type DeclaredInputOptions = {
  inputName: string;
  label?: string;
  materializedOptions: Record<string, DeclaredInputOption[]>;
};

/** Pending promotion request — the submission trigger `promotePolicy` reacts to. */
export type PendingPromotion = { subGoalId: string } | null;

/** Pending input values — the submission trigger `recordInputValuesPolicy` reacts to. */
export type PendingInputValues = { subGoalId: string; values: Record<string, unknown> } | null;

export type RequestsState = {
  data: {
    /** Sub-Goal rows, newest request first. Covers BOTH the list and the detail. */
    subGoals: AutomationRequestsReadmodelType[];
    /** Shadow evidence keyed by SUB-GOAL id. Absent key === not looked at yet. */
    evidence: Record<string, ShadowEvidence>;
  };
  ui: {
    /** The request the detail route is showing, or `null` on the list. */
    selectedRequestId: string | null;
    pendingPromotion: PendingPromotion;
    pendingInputValues: PendingInputValues;
    /** The Sub-Goal whose last input-values save the server refused, or `null`. */
    inputValuesError: { subGoalId: string } | null;
  };
  loading: {
    listLoading: boolean;
    detailLoading: boolean;
  };
};

export const initialState: RequestsState = {
  data: { subGoals: [], evidence: {}},
  ui: { selectedRequestId: null, pendingPromotion: null, pendingInputValues: null, inputValuesError: null },
  loading: { listLoading: false, detailLoading: false }
};
